/**
 * @file site-view-context.ts
 * @description Контекст экранов «вида сайта» (phase19.16 Р1): черновик,
 * «что изменено», права по частям и общие окна (публикация, «Что
 * изменено», отмена изменений, предпросмотр на весь экран).
 *
 * Один источник правды на все экраны группы «Сайт → Вид сайта»: полоса
 * черновика, статус в панели и окно публикации читают одно и то же, а
 * после сохранения части экран сообщает новую версию через `applyPart`.
 * Здесь же — части черновика с автосохранением и состояние предпросмотра:
 * панели не держат своих копий (подготовка к оболочке «Сайт →
 * Конструктор», фаза §7.14).
 */

import { createContext, useContext } from "react";

import type { SiteViewPart } from "@chaiberi/shared";

import type { SiteDraftParts } from "../../hooks/site-view/useSiteDraftParts";
import type { SitePreviewState } from "../../hooks/site-view/useSitePreviewState";
import type { SitePartState, SiteViewChanges, SiteViewDraft } from "../../lib/site-view/types";

/**
 * Конструктор в окне публикации (phase19.16 Р1-6а; Р3а — SB105, SB126):
 * открытая страница отмечена по умолчанию, после публикации конструктор
 * перечитывает страницу, список и канву. Вне конструктора `null` — окно само
 * решает по правам, показывать ли страницы с правками.
 */
export interface PublishPageTarget {
  /** Открытая страница; `null` — экран магазина или шаблон товара. */
  pageId: string | null;
  /** Есть право публикации контента (SB55). */
  canPublish: boolean;
  /** Есть право контента — без него список страниц не запрашиваем (403). */
  canView: boolean;
  /** Перечитать конструктор после публикации страниц. */
  afterPublish: () => Promise<void>;
  /**
   * Окно открыли кнопкой «Показать на сайте» (SB128, QA Р3а D13): у открытой
   * страницы сразу отмечены и пункт, и «Показывать на сайте». Признак
   * одноразовый — чтение его сбрасывает.
   */
  takeShowIntent?: () => boolean;
}

/** Какое общее окно открыто. */
export type SiteViewDialog = "publish" | "changes" | "discard" | "preview" | null;

export interface SiteViewContextValue {
  draft: SiteViewDraft | null;
  /** Части черновика с автосохранением (одно состояние на все панели); `null` — не загружен. */
  parts: SiteDraftParts | null;
  /** Предпросмотр: экран, ширина, черновик или сайт (один на все панели). */
  preview: SitePreviewState;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  changes: SiteViewChanges | null;
  /** Перечитать «что изменено» (с небольшой задержкой — после серии правок). */
  refreshChanges: () => void;
  /** Новая версия части и её значение после сохранения. */
  applyPart: (part: SitePartState, patch?: Partial<Pick<SiteViewDraft, "theme" | "brand" | "seo" | "blog" | "catalog" | "counters" | "consentPlatform" | "navigation">>) => void;
  /** Заменить черновик целиком (ответ «Отменить изменения»). */
  replaceDraft: (draft: SiteViewDraft) => void;
  /** Части, которые сотрудник может править. */
  editableParts: SiteViewPart[];
  currentUserId: string | null;
  /** Адрес сайта для «Открыть сайт ↗»; `null` — не настроен. */
  siteUrl: string | null;
  dialog: SiteViewDialog;
  openDialog: (dialog: SiteViewDialog) => void;
  pageTarget: PublishPageTarget | null;
  setPageTarget: (target: PublishPageTarget | null) => void;
}

export const SiteViewContext = createContext<SiteViewContextValue | null>(null);

/** Контекст вида сайта; вне каркаса — ошибка сборки страницы. */
export function useSiteView(): SiteViewContextValue {
  const ctx = useContext(SiteViewContext);
  if (!ctx) throw new Error("useSiteView(): страница вида сайта должна быть внутри SiteViewLayout");
  return ctx;
}

/** Есть изменения, которых нет на сайте. */
export function hasDraftChanges(changes: SiteViewChanges | null): boolean {
  return (changes?.changedParts.length ?? 0) > 0;
}

/**
 * Можно открыть окно публикации: есть изменения и нет чужих правок, на
 * которые не хватает права (SB60). Блокирующие проверки (реквизиты,
 * оферта) окно не закрывают — причину и ссылку человек видит в нём (uiux §6).
 */
export function canOpenPublish(changes: SiteViewChanges | null): boolean {
  return hasDraftChanges(changes) && (changes?.blockedBy.length ?? 0) === 0;
}
